import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Users,
  Crown,
  CheckCircle2,
  XCircle,
  Send,
  QrCode as QrIcon,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, get } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Button,
  Modal,
  Field,
  Input,
  Select,
  Table,
  Badge,
  StatusBadge,
  PersonCell,
  Loader,
  SearchInput,
  SectionTitle,
  Pagination,
  Stat,
  ConfirmModal,
} from '../../components/UI';
import { fmtNumber, pct } from '../../utils/format';

const BLANK = { name: '', email: '', mobile: '', employeeCode: '', designation: '', department: '', isVip: false };

export default function Participants() {
  const { eventId, events } = useEvents();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [docFilter, setDocFilter] = useState('');
  const [scopeEvent, setScopeEvent] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);


  const [qrFor, setQrFor] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [requestDocs, setRequestDocs] = useState(false);

  const params = {
    role: 'user',
    page,
    limit: 25,
    ...(search ? { search } : {}),
    ...(scopeEvent && eventId ? { event: eventId } : {}),
  };

  const { data: rows, meta, loading, reload } = useFetch('/users', params, { withMeta: true });

  const visible = docFilter
    ? (rows || []).filter((r) => (r.documents?.status || 'pending') === docFilter)
    : rows;

  const create = async () => {
    if (!form.name.trim() || !form.mobile.trim()) {
      return toast.error('Name and mobile number are required');
    }
    setSaving(true);
    try {
      await post('/users', { ...form, role: 'user', events: eventId ? [eventId] : [] });
      toast.success(`${form.name} added`);
      setAddOpen(false);
      setForm(BLANK);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const showQr = async (row) => {
    setQrFor(row);
    setQrImage(null);
    try {
      const d = await get(`/users/${row._id}/qr`);
      setQrImage(d.image);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const askForDocuments = async () => {
    try {
      const res = await post('/flights/request-documents', { event: eventId });
      toast.success(res.message);
      setRequestDocs(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const stats = {
    total: meta?.total || 0,
    vips: (rows || []).filter((r) => r.isVip).length,
    verified: (rows || []).filter((r) => r.documents?.status === 'verified').length,
    pending: (rows || []).filter((r) => ['pending', 'rejected'].includes(r.documents?.status || 'pending')).length,
  };

  const columns = [
    { key: 'name', title: 'Participant', render: (r) => <PersonCell person={r} /> },
    { key: 'mobile', title: 'Contact', render: (r) => (
      <div>
        <div style={{ fontSize: 12.5 }}>{r.mobile || '—'}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{r.email || ''}</div>
      </div>
    )},
    { key: 'department', title: 'Department', render: (r) => r.department || '—' },
    { key: 'docs', title: 'Documents', render: (r) => <StatusBadge status={r.documents?.status || 'pending'} /> },
    { key: 'vip', title: 'VIP', align: 'center', render: (r) => (
      r.isVip ? <Crown size={15} color="var(--gold-400)" /> : <span style={{ color: 'var(--text-lo)' }}>—</span>
    )},
    { key: 'actions', title: '', align: 'right', width: 60, render: (r) => (
      <button onClick={(e) => { e.stopPropagation(); showQr(r); }} title="Show digital pass"
        style={{ color: 'var(--gold-500)', padding: 6, display: 'flex' }}>
        <QrIcon size={16} />
      </button>
    )},
  ];

  return (
    <>
      <SectionTitle
        title="Participants"
        subtitle="Employees and guests attending your events"
        action={
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <Button variant="ghost" icon={Send} onClick={() => setRequestDocs(true)} disabled={!eventId}>
              Request documents
            </Button>
            <Button icon={Plus} onClick={() => { setForm(BLANK); setAddOpen(true); }}>Add person</Button>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
        <Stat label="Total" value={fmtNumber(stats.total)} icon={Users} tone="info" />
        <Stat label="VIP Guests" value={stats.vips} icon={Crown} tone="gold" />
        <Stat label="Docs Verified" value={stats.verified} icon={CheckCircle2} tone="success"
          progress={pct(stats.verified, rows?.length || 0)} />
        <Stat label="Docs Pending" value={stats.pending} icon={XCircle} tone="warning" />
      </div>

      <Card padding={16} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 11, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchInput
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, email, mobile or code…"
            style={{ flex: 1, minWidth: 220 }}
          />
          <Select value={docFilter} onChange={(e) => setDocFilter(e.target.value)} style={{ width: 172 }}>
            <option value="">All document states</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </Select>
          {events.length > 0 && (
            <>
              <EventPicker />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-mid)', cursor: 'pointer' }}>
                <input type="checkbox" checked={scopeEvent}
                  onChange={(e) => { setScopeEvent(e.target.checked); setPage(1); }}
                  style={{ width: 14, height: 14, accentColor: 'var(--gold-400)' }} />
                This event only
              </label>
            </>
          )}
        </div>
      </Card>

      <Table
        columns={columns}
        rows={visible}
        loading={loading}
        empty={search ? 'No participants match your search' : 'No participants yet — import your employee list to get started'}
      />
      <Pagination meta={meta} onChange={setPage} />

      {/* ── add person ───────────────────────────────────────────── */}
      <Modal
        open={addOpen} onClose={() => setAddOpen(false)}
        title="Add a participant"
        subtitle={eventId ? 'They will be added to the selected event' : 'Select an event first to assign them directly'}
        footer={
          <>
            <Button variant="subtle" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={create} loading={saving}>Add participant</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Full name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus placeholder="Aarav Sharma" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Mobile number" required hint="Used for WhatsApp OTP login">
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="9876543210" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="aarav@company.com" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Employee code">
              <Input value={form.employeeCode} onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} placeholder="MP1042" />
            </Field>
            <Field label="Department">
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Sales" />
            </Field>
          </div>
          <Field label="Designation">
            <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="Regional Manager" />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.isVip} onChange={(e) => setForm({ ...form, isVip: e.target.checked })}
              style={{ width: 15, height: 15, accentColor: 'var(--gold-400)' }} />
            <Crown size={14} color="var(--gold-400)" />
            Mark as a VIP guest
          </label>
        </div>
      </Modal>

      {/* ── QR pass ──────────────────────────────────────────────── */}
      <Modal open={Boolean(qrFor)} onClose={() => setQrFor(null)} title="Digital event pass" subtitle={qrFor?.name} width={392}>
        <div style={{ textAlign: 'center' }}>
          {qrImage ? (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              style={{ display: 'inline-block', padding: 16, background: '#fff', borderRadius: 'var(--r-lg)' }}>
              <img src={qrImage} alt="Attendee QR" style={{ width: 210, height: 210, display: 'block' }} />
            </motion.div>
          ) : <Loader label="Generating pass" />}
          <p style={{ fontSize: 15, fontWeight: 600, marginTop: 17 }}>{qrFor?.name}</p>
          <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 3 }}>
            {qrFor?.designation || qrFor?.employeeCode || qrFor?.mobile}
          </p>
          {qrFor?.isVip && <Badge tone="gold" style={{ marginTop: 10 }}><Crown size={11} /> VIP Guest</Badge>}
          <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 15, lineHeight: 1.6 }}>
            This code is scanned at event entry, session halls, bus boarding and
            the hotel desk.
          </p>
        </div>
      </Modal>

      <ConfirmModal
        open={requestDocs} onClose={() => setRequestDocs(false)} onConfirm={askForDocuments}
        title="Request travel documents" confirmLabel="Send request"
        message="Everyone on this event whose documents are still pending or rejected will get a WhatsApp, email and push reminder to upload their Aadhaar or passport."
      />
    </>
  );
}
